import { useCallback, useMemo } from "react";
import {
  catalogBySlug,
  proposeTodaySession,
  type Exercise,
  type PainMap,
  type PlanStarter,
  type TodayGap,
} from "@heraion/shared";

import type { MuscleCoverage } from "../Progress/muscleCoverage";
import { todayGapsFromCoverage } from "../Progress/todayGapsFromCoverage";
import type { StartBuiltSessionInput } from "./useWorkoutStarter";

// What the empty «today» card needs from the host's coverage (D-c): whether
// «Build one anyway» has anything to build, the build itself, and whether no
// muscle is under its weekly minimum. Shared by the mobile and web mounts; the
// starter seam (useWorkoutStarter.buildTodayStarter) is theirs. A draft with
// candidates builds «anyway» on its own first (useTodayDraft).

/** What the empty card reads from the host. */
export type TodayEmptyHost = {
  onBuildAnyway: (() => Promise<PlanStarter | null>) | undefined;
  weeklyMinimumMet: boolean;
};

/** The host before its coverage is loaded: no button, no fact. */
export const IDLE_TODAY_EMPTY_HOST: TodayEmptyHost = {
  onBuildAnyway: undefined,
  weeklyMinimumMet: false,
};

export function useTodayAnyway({
  coverage,
  catalog,
  painMap,
  bodyweightOnly = false,
  buildTodayStarter,
}: {
  /** #492 F5: scoped as the draft was (withBodyweightOnly), by the host. */
  coverage: MuscleCoverage | null;
  catalog: ReadonlyArray<Exercise> | null;
  painMap: PainMap | undefined;
  /** The draft was bodyweight only: the rebuilt one says so too. */
  bodyweightOnly?: boolean;
  buildTodayStarter: (input: StartBuiltSessionInput) => Promise<PlanStarter | undefined>;
}): TodayEmptyHost {
  // Antenore 07/10: «anyway» ignores the rest. It builds today's own gaps, most
  // rested first; with none under MEV, a MAV gap from every banded row. Rest
  // ignored, only the pain map can empty it, so an empty history is enough to
  // know the button would build something.
  const index = useMemo(() => (catalog ? catalogBySlug(catalog) : null), [catalog]);
  const { gaps, viable } = useMemo(() => {
    const builds = (g: TodayGap[]) =>
      index !== null &&
      g.length > 0 &&
      proposeTodaySession({
        gaps: g,
        catalog: index,
        painMap,
        history: new Map(),
        nowMs: 0,
        ignoreRest: true,
      }).view.rows.length > 0;
    const own = todayGapsFromCoverage(coverage);
    if (builds(own)) return { gaps: own, viable: true };
    const anyway = todayGapsFromCoverage(coverage, { mode: "anyway" });
    return { gaps: anyway, viable: builds(anyway) };
  }, [coverage, index, painMap]);
  const build = useCallback(
    async () =>
      catalog
        ? ((await buildTodayStarter({
            gaps,
            catalog,
            painMap,
            ignoreRest: true,
            ...(bodyweightOnly ? { bodyweightOnly: true } : {}),
          })) ?? null)
        : null,
    [buildTodayStarter, gaps, catalog, painMap, bodyweightOnly],
  );
  return {
    onBuildAnyway: viable ? build : undefined,
    weeklyMinimumMet: coverage !== null && !coverage.incomplete && coverage.belowMevCount === 0,
  };
}
